import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Avatar,
  HStack,
  Image,
  Spinner,
  Portal,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import CheckMark from "@root/assets/icons/checkMark.svg";
import { DataTable } from "@root/components/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { useJournalistContext } from "@root/context/journalist-context";
import { formatDateToISO } from "@root/utils/date-helper";
import SearchInput from "../common/SearchInput";
import { ModalDelete } from "@root/components/modal-delete";
import JournalistMenu from "./JournalistMenu";
import JournalistStatusBadge from "./JournalistStatusBadge";
import { RECORDS_PER_PAGE } from "@root/constants";
import { usePledgeJournalistManagementTourGuide } from "@root/hooks/usePledgeJournalistManagementTourGuide";
import { TourGuide, RestartTourButton } from "@root/components/tour-guide";
const JournalistManagment = () => {
  const { t, i18n } = useTranslation("common");
  const { pledgeJournalistManagementTourGuide, isMobile: isMobileTour } =
    usePledgeJournalistManagementTourGuide();
  const {
    journalists,
    fetchJournalists,
    updateJournalistStatus,
    deleteJournalist,
    pageNo,
    pageCount,
    setPageNo,
    loader,
  } = useJournalistContext();

  const [searchValue, setSearchValue] = useState("");
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
  const [loadingJournalistId, setLoadingJournalistId] = useState<
    string | number | null
  >(null);
  const pageSize = RECORDS_PER_PAGE;

  const payload = {
    offset: (pageNo - 1) * pageSize,
    limit: pageSize,
  };

  useEffect(() => {
    fetchJournalists(payload);
  }, []);

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
    const payload = {
      offset: (newPage - 1) * pageSize,
      limit: pageSize,
    };
    fetchJournalists(payload);
  };

  const filteredJournalists = useMemo(() => {
    let data = journalists?.rows || [];
    if (searchValue.trim()) {
      const search = searchValue.trim().toLowerCase();
      data = data.filter(
        (journalist: any) =>
          journalist.name?.toLowerCase().includes(search) ||
          journalist.email?.toLowerCase().includes(search),
      );
    }
    return data;
  }, [journalists?.rows, searchValue]);

  // Wrapper function to handle status updates with loading state
  const handleUpdateJournalistStatus = async (
    id: string | number,
    status: number,
  ) => {
    setLoadingJournalistId(id);
    try {
      await updateJournalistStatus(id, status, payload);
    } finally {
      setLoadingJournalistId(null);
    }
  };

  // Wrapper function to handle delete with loading state
  const handleDeleteJournalist = async (id: string | number) => {
    setLoadingJournalistId(id);
    try {
      await deleteJournalist(id, payload);
      setDeleteModal({ isOpen: false, id: null });
    } finally {
      setLoadingJournalistId(null);
    }
  };

  const columns = useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        header: t("journalistRequests.name"),
        accessorKey: "name",
        cell: ({ row }) => (
          <HStack>
            <Avatar
              size="sm"
              name={row?.original?.name}
              src={row?.original?.avatar}
            />
            <Box>
              <Text fontWeight="500">{row?.original?.name}</Text>
              <Text fontSize="sm" color="gray.500">
                {row?.original?.username}
              </Text>
            </Box>
          </HStack>
        ),
      },
      {
        header: t("journalistRequests.email"),
        accessorKey: "email",
        cell: ({ row }) => row?.original?.email,
      },
      {
        header: t("journalistRequests.requestDate"),
        accessorKey: "date",
        cell: ({ row }) => formatDateToISO(row?.original?.createdAt),
      },
      {
        header: t("journalistRequests.status"),
        accessorKey: "status",
        cell: ({ row }) => (
          <JournalistStatusBadge
            status={row?.original?.status}
            isVerified={row?.original?.isVerified}
            t={t}
          />
        ),
      },
      {
        header: t("journalistRequests.menu"),
        id: "menu",
        cell: ({ row }) =>
          // Approving is impossible until the email OTP is entered — the API
          // rejects it — so don't offer the action.
          row?.original?.isVerified === false ? null : (
            <JournalistMenu
              status={row?.original?.status}
              id={row?.original?.id}
              updateJournalistStatus={handleUpdateJournalistStatus}
              setDeleteModal={setDeleteModal}
              isLoading={loadingJournalistId === row?.original?.id}
              t={t}
            />
          ),
      },
    ],
    [t, i18n.language, loadingJournalistId, handleUpdateJournalistStatus],
  );

  const noRequestFound = () => (
    <Flex
      flexDir={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"350px"}
    >
      <Image src={CheckMark} />
      <Text fontSize={"24px"} fontWeight={600} mt={3}>
        {t("journalistRequests.noRecentRequests")}
      </Text>
      <Text fontSize={"16px"} fontWeight={400} mt={1}>
        {t("journalistRequests.noApprovalRequestYet")}
      </Text>
    </Flex>
  );

  return (
    <Flex direction="column" height="100%" py={4} px={8}>
      <Text fontWeight={600} fontSize="24px" mb={1}>
        {t("AdminSidebar.journalistApprovalRequest")}
      </Text>
      <Text color="gray.500" mb={6}>
        {t("journalistRequests.description")}
      </Text>
      <Flex
        mb={4}
        gap={2}
        flexWrap={{ base: "wrap", md: "nowrap" }}
        align="center"
      >
        <SearchInput
          data-tour-item="pledge-journalist-mgmt-search"
          maxW="340px"
          placeholder={t("journalistRequests.searchBy")}
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchValue(e.target.value)
          }
        />
      </Flex>

      <Box
        p={0}
        overflowX="auto"
        bg="white"
        borderRadius="16px"
        minW="320px"
        maxH={"700px"}
        data-tour-item="pledge-journalist-mgmt-table"
      >
        {loader ? (
          <Flex
            flexDir={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"350px"}
            gap={3}
          >
            <Spinner size="lg" color="#1819B4" />
            <Text fontSize={"16px"} fontWeight={400} color="#798692">
              {t("loading")}
            </Text>
          </Flex>
        ) : filteredJournalists?.length > 0 ? (
          <DataTable
            data={filteredJournalists}
            columns={columns}
            showPagination={true}
            page={pageNo}
            pageCount={pageCount}
            handlePageChange={handlePageChange}
            pageSize={pageSize}
            canEditRow={false}
            getRowId={(row: any) => row?.email}
            renderSubComponent={undefined}
            getRowCanExpand={undefined}
            getRowSelected={undefined}
            onRowSelectionChange={undefined}
          />
        ) : (
          noRequestFound()
        )}
      </Box>
      {!isMobileTour && !pledgeJournalistManagementTourGuide.isActive && (
        <Portal>
          <RestartTourButton
            onClick={pledgeJournalistManagementTourGuide.startTour}
            position={{ bottom: "24px", right: "24px" }}
          />
        </Portal>
      )}
      {!isMobileTour &&
        pledgeJournalistManagementTourGuide.isActive &&
        pledgeJournalistManagementTourGuide.currentStepData && (
          <TourGuide
            isActive={pledgeJournalistManagementTourGuide.isActive}
            currentStep={pledgeJournalistManagementTourGuide.currentStepData}
            currentStepIndex={pledgeJournalistManagementTourGuide.currentStep}
            totalSteps={pledgeJournalistManagementTourGuide.totalSteps}
            onNext={pledgeJournalistManagementTourGuide.nextStep}
            onBack={pledgeJournalistManagementTourGuide.previousStep}
            onClose={pledgeJournalistManagementTourGuide.stopTour}
            targetSelector={
              pledgeJournalistManagementTourGuide.currentStepData.targetSelector
            }
            borderRadius="8px"
          />
        )}
      <ModalDelete
        isOpen={deleteModal.isOpen}
        onClose={() => {
          if (!loadingJournalistId) {
            setDeleteModal({ isOpen: false, id: null });
          }
        }}
        onConfirm={async () => {
          if (deleteModal.id) {
            await handleDeleteJournalist(deleteModal.id);
          }
        }}
        title={t("journalistRequests.deleteTitle")}
        description={t("journalistRequests.deleteDescription")}
        deleteButtonTitle={t("journalistRequests.delete")}
        cancelButtonTitle={t("journalistRequests.cancel")}
      />
    </Flex>
  );
};

export default JournalistManagment;
